import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { servicePlansService } from "../services/servicePlansService";
import { useAuth } from "./AuthContext";
import type {
  UserSubscription,
  ServicePlan,
} from "../../../shared/types/servicePlans";
import {
  setTokenBalanceUpdater,
  setTokenBalanceSyncer,
} from "../utils/tokenUpdate";

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  plans: ServicePlan[];
  usage: Record<
    string,
    {
      used: number;
      limit: number | null;
      type: "total" | "monthly";
      resetDate?: string;
    }
  >;
  isLoadingSubscription: boolean;
  isLoadingPlans: boolean;
  isLoadingUsage: boolean;
  error: Error | null;
  reloadSubscription: (
    preserveOnFailure?: boolean,
    forceRefresh?: boolean,
    isBackgroundRefresh?: boolean,
  ) => Promise<void>;
  reloadPlans: () => Promise<void>;
  reloadUsage: () => Promise<void>;
  reloadAll: () => Promise<void>;
  getRemainingQuotas: (currentCounts: {
    resumes: number;
    jobApplications: number;
  }) => {
    resumes: number | null;
    jobApplications: number | null;
  };
  isFeatureAvailable: (
    feature:
      | "intelligentSearch"
      | "intelligentCompanySearch"
      | "resumeJobMatch"
      | "resumeAnalysis",
  ) => boolean;
  getFeatureRemainingQuota: (
    feature:
      | "intelligentSearch"
      | "intelligentCompanySearch"
      | "resumeJobMatch"
      | "resumeAnalysis",
    currentUsage?: number,
  ) => number | null;
  isLimitExceeded: (
    type: "resumes" | "jobApplications",
    currentCount: number,
  ) => boolean;
  getPlanById: (planId: string) => ServicePlan | undefined;
  updateTokenBalanceOptimistically: (tokensCharged: number) => void;
  syncTokenBalanceFromBackend: (
    tokensRemaining: number,
    tokensUsed: number,
  ) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null,
  );
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [usage, setUsage] = useState<
    Record<
      string,
      {
        used: number;
        limit: number | null;
        type: "total" | "monthly";
        resetDate?: string;
      }
    >
  >({});
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Ref to track current subscription without causing dependency issues
  const subscriptionRef = useRef<UserSubscription | null>(null);

  // Ref to track loading state without causing dependency issues
  // CRITICAL: Initialize to match initial state
  const isLoadingSubscriptionRef = useRef<boolean>(true);
  const loadingStartTimeRef = useRef<number | null>(null);
  const isBackgroundRefreshingRef = useRef<boolean>(false);
  const lastBackgroundRefreshAtRef = useRef<number>(0);

  // Ref to track if subscription has been loaded at least once (distinguishes initial load from background refresh)
  const isInitialLoadRef = useRef<boolean>(true);

  // Update refs whenever state changes
  useEffect(() => {
    subscriptionRef.current = subscription;
  }, [subscription]);

  useEffect(() => {
    isLoadingSubscriptionRef.current = isLoadingSubscription;
    if (isLoadingSubscription) {
      // Only set start time if not already set (to track when loading started)
      if (!loadingStartTimeRef.current) {
        loadingStartTimeRef.current = Date.now();
      }
    } else {
      loadingStartTimeRef.current = null;
    }
  }, [isLoadingSubscription]);

  // Safeguard: Reset loading state if it's been stuck for more than 30 seconds
  useEffect(() => {
    const checkStuckLoading = setInterval(() => {
      if (isLoadingSubscriptionRef.current && loadingStartTimeRef.current) {
        const stuckDuration = Date.now() - loadingStartTimeRef.current;
        if (stuckDuration > 30000) {
          // 30 seconds
          setIsLoadingSubscription(false);
          isLoadingSubscriptionRef.current = false;
          loadingStartTimeRef.current = null;
        }
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkStuckLoading);
  }, []);

  // Load subscription
  const reloadSubscription = useCallback(
    async (
      preserveOnFailure: boolean = false,
      forceRefresh: boolean = false,
      isBackgroundRefresh: boolean = false,
    ) => {
      // Only fetch subscription if user is authenticated
      if (!isAuthenticated) {
        setIsLoadingSubscription(false);
        isLoadingSubscriptionRef.current = false;
        isBackgroundRefreshingRef.current = false;
        setSubscription(null);
        return;
      }

      // CRITICAL: If forceRefresh is true, bypass any cached data and fetch fresh from database
      // This ensures we always get the latest token balance, even if subscription state exists
      if (forceRefresh) {
        // Clear current subscription to force a fresh fetch
        // This prevents the API from returning cached data
      }

      // CRITICAL: Atomic check-and-set to prevent multiple simultaneous calls
      // Only skip if already loading AND we have a subscription (prevents duplicate calls)
      // Allow reload if loading but no subscription yet (initial load or stuck state)
      if (isLoadingSubscriptionRef.current && subscriptionRef.current) {
        // Already loading and have subscription - skip duplicate call
        return;
      }

      // Background refreshes are silent, so they need their own in-flight/throttle guard.
      // Without this, multiple silent callers can stack and churn the sidebar cards.
      if (isBackgroundRefresh) {
        const now = Date.now();
        if (isBackgroundRefreshingRef.current) {
          return;
        }
        if (now - lastBackgroundRefreshAtRef.current < 5000) {
          return;
        }
        isBackgroundRefreshingRef.current = true;
        lastBackgroundRefreshAtRef.current = now;
      }

      // CRITICAL: Only set loading state on initial load, NOT during background refresh
      // Background refresh should be completely silent - no loading state, no UI interruption
      if (!isBackgroundRefresh) {
        // Set loading state immediately (atomic operation)
        // This ensures loading state is true even if it was set in useEffect
        setIsLoadingSubscription(true);
        isLoadingSubscriptionRef.current = true;
        if (!loadingStartTimeRef.current) {
          loadingStartTimeRef.current = Date.now();
        }
      }

      // Store current subscription to preserve it on failure if requested
      // Use ref to avoid dependency on subscription state
      const currentSubscription = subscriptionRef.current;

      try {
        setError(null);

        // CRITICAL: Refresh token before loading subscription to prevent stale token errors
        // This ensures we have a valid token before checking subscription status
        const token = localStorage.getItem("token");
        if (token) {
          try {
            // Check if token is expired or expiring soon
            const parts = token.split(".");
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              if (payload.exp) {
                const expirationTime = payload.exp * 1000;
                const now = Date.now();
                const isExpired = now >= expirationTime;
                const expiresSoon = expirationTime - now < 5 * 60 * 1000; // 5 minutes

                if (isExpired || expiresSoon) {
                  const { tokenRefreshService } = await import(
                    "../services/tokenRefreshService"
                  );
                  await tokenRefreshService.forceRefresh();
                  // Wait a bit for token to be fully updated in apiService
                  await new Promise((resolve) => setTimeout(resolve, 200));
                }
              }
            }
          } catch (tokenCheckError) {
            // If token check fails, try to refresh anyway
            try {
              const { tokenRefreshService } = await import(
                "../services/tokenRefreshService"
              );
              await tokenRefreshService.forceRefresh();
              // Wait a bit for token to be fully updated in apiService
              await new Promise((resolve) => setTimeout(resolve, 200));
            } catch (refreshError) {
              // Token refresh failed - proceeding with subscription check anyway
            }
          }
        }

        const data = await servicePlansService.getUserSubscription();

        // CRITICAL: Log subscription data to help debug validity issues
        // This helps identify if API returns invalid data or if validity check is wrong
        if (data) {
          const { isValidSubscription } = await import(
            "../utils/subscriptionUtils"
          );
          const isValid = isValidSubscription(data);
          if (!isValid) {
            console.warn(
              "[SubscriptionContext] Subscription loaded but failed validity check:",
              {
                currentPlanId: data.currentPlanId,
                status: data.status,
                tokensRemaining: data.tokensRemaining,
              },
            );
          }
        }

        // CRITICAL: Only update subscription state if data actually changed (prevent unnecessary re-renders)
        // This is especially important for background refresh to avoid triggering UI updates
        const currentSubscription = subscriptionRef.current;
        const hasChanged =
          !currentSubscription ||
          currentSubscription.tokensRemaining !== data?.tokensRemaining ||
          currentSubscription.tokensUsed !== data?.tokensUsed ||
          currentSubscription.currentPlanId !== data?.currentPlanId ||
          currentSubscription.status !== data?.status ||
          JSON.stringify(currentSubscription.effectiveLimits) !==
            JSON.stringify(data?.effectiveLimits);

        if (hasChanged) {
          setSubscription(data);
          // Mark that we've completed at least one successful load
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        } else if (isBackgroundRefresh) {
          // Silently skip update if data hasn't changed during background refresh
          // This prevents unnecessary re-renders
        }
      } catch (err: any) {
        // CRITICAL: During background refresh, handle failures completely silently
        // Don't update error state, don't clear subscription, don't trigger any UI updates
        if (isBackgroundRefresh) {
          // Silently fail - preserve existing subscription, don't update any state
          // Log debug only if logging is enabled
          import("../utils/logger")
            .then(({ logger }) => {
              if (logger.isEnabled()) {
                logger.debug(
                  "Background subscription refresh failed silently:",
                  err,
                );
              }
            })
            .catch(() => {});
          // Don't update any state - subscription remains unchanged
          return; // Exit early, don't run finally block that sets loading to false
        }

        // CRITICAL FIX: If preserveOnFailure is true and we have a current subscription,
        // preserve it instead of clearing it. This prevents remounts when reloading
        // after token charges fails (e.g., due to network issues or temporary API errors).
        if (preserveOnFailure && currentSubscription) {
          // CRITICAL: Don't call setSubscription() if we're preserving - it's already the current value
          // Calling setSubscription() even with the same reference can trigger unnecessary re-renders
          // Just log the error and return - the subscription state remains unchanged
          // Log warning only if logging is enabled (via centralized logger)
          import("../utils/logger")
            .then(({ logger }) => {
              if (logger.isEnabled()) {
                logger.warn(
                  "Failed to reload subscription, preserving existing subscription:",
                  err,
                );
              }
            })
            .catch(() => {});
          // Don't update subscription state - it's already correct
        } else {
          // CRITICAL FIX: Only clear subscription if we don't already have one
          // If we have a current subscription and reload fails, preserve it to prevent
          // MainContent from redirecting to service-plans (which causes page reloads)
          // Only clear if we're certain the user doesn't have a subscription
          if (err.status === 404 || err.status === 403) {
            // 404/403 might mean new user, but if we already have a subscription,
            // it's likely a temporary error - preserve it
            if (!currentSubscription) {
              setSubscription(null);
            } else {
              // Preserve existing subscription - don't clear on temporary errors
              // Log warning only if logging is enabled
              import("../utils/logger")
                .then(({ logger }) => {
                  if (logger.isEnabled()) {
                    logger.warn(
                      "Subscription reload failed with 404/403, preserving existing subscription:",
                      err,
                    );
                  }
                })
                .catch(() => {});
            }
            // Suppress log for expected case (new user without subscription)
          } else if (err.status === 401) {
            // Token expired or not authenticated
            // If we have a subscription, it might just be a token refresh issue - preserve it
            if (!currentSubscription) {
              setSubscription(null);
            } else {
              // Preserve existing subscription - token refresh might fix this
              // Log warning only if logging is enabled
              import("../utils/logger")
                .then(({ logger }) => {
                  if (logger.isEnabled()) {
                    logger.warn(
                      "Subscription reload failed with 401, preserving existing subscription:",
                      err,
                    );
                  }
                })
                .catch(() => {});
            }
            // Suppress log for expected case (not authenticated)
          } else {
            // Other errors - preserve subscription if we have one
            if (currentSubscription) {
              // Log warning only if logging is enabled
              import("../utils/logger")
                .then(({ logger }) => {
                  if (logger.isEnabled()) {
                    logger.warn(
                      "Subscription reload failed, preserving existing subscription:",
                      err,
                    );
                  }
                })
                .catch(() => {});
              // Don't clear subscription on network/other errors
            } else {
              setError(err);
            }
          }
        }
      } finally {
        // CRITICAL: Only update loading state if this was NOT a background refresh
        // Background refresh should never set loading state (it's completely silent)
        if (!isBackgroundRefresh) {
          setIsLoadingSubscription(false);
          isLoadingSubscriptionRef.current = false;
          loadingStartTimeRef.current = null;
          // Mark that we've completed at least one load attempt (even if it failed)
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        } else {
          isBackgroundRefreshingRef.current = false;
        }
      }
    },
    [isAuthenticated],
  ); // Removed isLoadingSubscription from deps - using ref instead

  // Load plans
  const reloadPlans = useCallback(async () => {
    // Plans can be loaded even if user is not authenticated (public data)
    try {
      setIsLoadingPlans(true);
      setError(null);
      const data = await servicePlansService.getServicePlans();
      const sortedPlans = data.sort((a, b) => parseInt(a.id) - parseInt(b.id));
      setPlans(sortedPlans);
    } catch (err: any) {
      // Don't set error for 401 (not authenticated) - plans endpoint is public, 401 is expected when not logged in
      // Also don't set error for 403/404 (shouldn't happen for public endpoint, but handle gracefully)
      if (err.status === 401 || err.status === 403 || err.status === 404) {
        // Suppress log for expected case (not authenticated, but plans endpoint is public)
        setPlans([]);
      } else {
        setError(err);
      }
    } finally {
      setIsLoadingPlans(false);
    }
  }, []);

  // Load usage data
  const reloadUsage = useCallback(async () => {
    if (!isAuthenticated) {
      setUsage({});
      setIsLoadingUsage(false);
      return;
    }

    try {
      setIsLoadingUsage(true);
      setError(null);
      const usageData = await servicePlansService.getUserUsage();
      // Suppress debug log (only log if needed for debugging)
      setUsage(usageData);
    } catch (err: any) {
      // 403/404 = no subscription, so no usage data available (expected for new users)
      // 401 = not authenticated
      if (err.status === 404 || err.status === 403 || err.status === 401) {
        setUsage({});
      } else {
        // Don't set error for usage - it's not critical
        // Ensure usage is always an object, never undefined/null
        setUsage({});
      }
    } finally {
      setIsLoadingUsage(false);
    }
  }, [isAuthenticated]);

  // Reload both subscription and plans
  const reloadAll = useCallback(async () => {
    await Promise.all([reloadSubscription(), reloadPlans(), reloadUsage()]);
  }, [reloadSubscription, reloadPlans, reloadUsage]);

  // Load subscription and plans on mount or when authentication changes
  // CRITICAL: Force refresh on every page load to ensure we get the latest token balance from database
  useEffect(() => {
    // Only reload if authenticated (prevents unnecessary calls when not authenticated)
    if (isAuthenticated) {
      // CRITICAL: Set loading state to true BEFORE starting the load
      // This ensures MainContent shows hourglass immediately, even during the 100ms delay
      // Reset loading state if it was stuck from a previous attempt
      if (
        isLoadingSubscriptionRef.current &&
        !subscriptionRef.current &&
        loadingStartTimeRef.current
      ) {
        const stuckDuration = Date.now() - loadingStartTimeRef.current;
        // Only reset if stuck for more than 30 seconds (truly stuck, not just initial load)
        if (stuckDuration > 30000) {
          setIsLoadingSubscription(false);
          isLoadingSubscriptionRef.current = false;
          loadingStartTimeRef.current = null;
        }
      }

      // CRITICAL: Ensure loading state is true when starting subscription load
      // This prevents the gap where isLoadingSubscription is false but subscription hasn't loaded yet
      if (!isLoadingSubscriptionRef.current) {
        setIsLoadingSubscription(true);
        isLoadingSubscriptionRef.current = true;
        if (!loadingStartTimeRef.current) {
          loadingStartTimeRef.current = Date.now();
        }
      }

      // CRITICAL: Force refresh on mount to ensure we get the latest data from the database
      // This bypasses any cached subscription data and fetches fresh token balance
      // Use a small delay to ensure auth token is fully loaded
      const timer = setTimeout(() => {
        reloadSubscription(false, true); // forceRefresh = true to bypass cache
        reloadPlans();
        reloadUsage();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Not authenticated - clear ALL subscription-related state
      // CRITICAL: Clear subscription state to prevent stale data from previous sessions
      // This ensures first login after logout works correctly
      setIsLoadingSubscription(false);
      isLoadingSubscriptionRef.current = false;
      setIsLoadingPlans(false);
      setIsLoadingUsage(false);
      setSubscription(null);
      subscriptionRef.current = null;
      setUsage({});
    }
  }, [isAuthenticated]); // CRITICAL: Only depend on isAuthenticated to prevent infinite loops

  // CRITICAL: Reload subscription when page becomes visible (user returns to tab)
  // This ensures token balances are always up-to-date when user switches back to the app
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Page became visible - reload subscription to get latest token balance
        // Use a small delay to avoid race conditions with other reloads
        setTimeout(() => {
          // CRITICAL: Pass isBackgroundRefresh = true for visibility change refresh
          // This makes the refresh silent (no loading state, no UI interruption)
          reloadSubscription(false, false, true).catch((err) => {
            // Silently fail - don't show errors for background refresh
            // Log debug only if logging is enabled
            import("../utils/logger")
              .then(({ logger }) => {
                if (logger.isEnabled()) {
                  logger.debug("Background subscription refresh failed:", err);
                }
              })
              .catch(() => {});
          });
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, reloadSubscription]);

  // CRITICAL: Periodic refresh of subscription (every 2 minutes) to ensure token balance is current
  // This prevents stale data from persisting in the UI
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(
      () => {
        // Only reload if not currently loading to avoid race conditions
        if (!isLoadingSubscriptionRef.current) {
          // CRITICAL: Pass isBackgroundRefresh = true to make refresh completely silent
          // This prevents loading state updates and UI interruptions
          reloadSubscription(false, false, true).catch((err) => {
            // Silently fail - don't show errors for background refresh
            // Log debug only if logging is enabled
            import("../utils/logger")
              .then(({ logger }) => {
                if (logger.isEnabled()) {
                  logger.debug("Periodic subscription refresh failed:", err);
                }
              })
              .catch(() => {});
          });
        }
      },
      2 * 60 * 1000,
    ); // 2 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated, reloadSubscription]);

  // No plan-based limits -- everything is unlimited, token balance is the only gate
  const getRemainingQuotas = useCallback(
    (_currentCounts: { resumes: number; jobApplications: number }) => {
      return {
        resumes: null,
        jobApplications: null,
      };
    },
    [],
  );

  // All features are always available -- token balance is checked on the backend
  const isFeatureAvailable = useCallback(
    (
      _feature:
        | "intelligentSearch"
        | "intelligentCompanySearch"
        | "resumeJobMatch"
        | "resumeAnalysis",
    ) => {
      return true;
    },
    [],
  );

  // No plan-based quotas -- always return null (unlimited)
  const getFeatureRemainingQuota = useCallback(
    (
      _feature:
        | "intelligentSearch"
        | "intelligentCompanySearch"
        | "resumeJobMatch"
        | "resumeAnalysis",
      _currentUsage?: number,
    ) => {
      return null;
    },
    [],
  );

  // No plan-based limits -- never exceeded, token balance is the only gate
  const isLimitExceeded = useCallback(
    (_type: "resumes" | "jobApplications", _currentCount: number) => {
      return false;
    },
    [],
  );

  // Get plan by ID
  const getPlanById = useCallback(
    (planId: string) => {
      return plans.find((plan) => plan.id === planId);
    },
    [plans],
  );

  // Update token balance optimistically (for real-time UI updates)
  const updateTokenBalanceOptimistically = useCallback(
    (tokensCharged: number) => {
      setSubscription((prevSubscription) => {
        if (!prevSubscription) {
          return prevSubscription;
        }

        const newTokensRemaining = Math.max(
          0,
          (prevSubscription.tokensRemaining || 0) - tokensCharged,
        );
        const newTokensUsed =
          (prevSubscription.tokensUsed || 0) + tokensCharged;

        return {
          ...prevSubscription,
          tokensRemaining: newTokensRemaining,
          tokensUsed: newTokensUsed,
        };
      });

      // REMOVED: Subscription reload after token charge
      // Token balance updates are optimistic and should NOT trigger any reloads or remounts
      // The backend has the correct balance, and it will be synced on next natural reload
      // (e.g., when user navigates, manually refreshes, or on next page load)
      // This prevents ApplicationsPage from unmounting when interview prep loads
    },
    [],
  );

  // Sync token balance from backend response (ensures accuracy after optimistic update)
  const syncTokenBalanceFromBackend = useCallback(
    (tokensRemaining: number, tokensUsed: number) => {
      setSubscription((prevSubscription) => {
        if (!prevSubscription) {
          return prevSubscription;
        }

        return {
          ...prevSubscription,
          tokensRemaining,
          tokensUsed,
        };
      });
    },
    [],
  );

  // Set token updater and syncer for services to use
  useEffect(() => {
    setTokenBalanceUpdater(updateTokenBalanceOptimistically);
    setTokenBalanceSyncer(syncTokenBalanceFromBackend);
    return () => {
      setTokenBalanceUpdater(() => {}); // Clear on unmount
      setTokenBalanceSyncer(() => {}); // Clear on unmount
    };
  }, [updateTokenBalanceOptimistically, syncTokenBalanceFromBackend]);

  const value: SubscriptionContextType = {
    subscription,
    plans,
    usage,
    isLoadingSubscription,
    isLoadingPlans,
    isLoadingUsage,
    error,
    reloadSubscription,
    reloadPlans,
    reloadUsage,
    reloadAll,
    getRemainingQuotas,
    isFeatureAvailable,
    getFeatureRemainingQuota,
    isLimitExceeded,
    getPlanById,
    updateTokenBalanceOptimistically,
    syncTokenBalanceFromBackend,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscriptionContext must be used within a SubscriptionProvider",
    );
  }
  return context;
};
